import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
    signal,
    ViewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Location } from "../../../pojo/Location";
import { Bind } from "../../../decorators/bind.decorator";
import { S25Util } from "../../../util/s25-util";
import { SpaceService } from "../../../services/space.service";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Table } from "../../s25-table/Table";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { S25EditableNumberComponent } from "../../s25-editable/s25-editable-number/s25.editable.number.component";
import { S25EditableDurationComponent } from "../../s25-editable/s25-editable-duration/s25.editable.duration.component";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25DropdownSearchCriteriaComponent } from "../../s25-dropdown/single-select/s25.dropdown.search.criteria.component";
import { S25ObjectDetailsService } from "../s25.object.details.service";
import Layout = Location.Layout;

@TypeManagerDecorator("s25-ng-location-details-layouts")
@Component({
    selector: "s25-ng-location-details-layouts",
    template: `
        @if (init()) {
            <label class="capacity">
                <span class="label-text" [class.c-margin-bottom--half]="!canEdit">Max Capacity</span>
                <s25-ng-editable-number
                    [(val)]="maxCapacity"
                    [readOnly]="!canEdit"
                    [hasCommit]="true"
                    [onCommit]="saveCapacity"
                    [ariaLabel]="'Max Capacity'"
                ></s25-ng-editable-number>
            </label>
            @if (spaceLayouts().length) {
                <label class="default-dropdown c-margin-bottom--quarter"
                    ><span class="label-text">Default Layout</span>
                    @if (canEdit && showAddLayout()) {
                        <s25-generic-dropdown
                            [(items)]="defaultOptions"
                            [(value)]="defaultLayout"
                            (valueChange)="saveDefault($event)"
                            [ariaLabel]="'Default Layout'"
                        />
                    } @else if (defaultLayout) {
                        <p>{{ defaultLayout.itemName }}</p>
                    }
                </label>
            }
            @if (spaceLayouts().length > 1) {
                <p class="ngFinePrint">Click on a layout name to view its details in the Layout Details panel</p>
            }
            <s25-ng-table [dataSource]="tableConfig"></s25-ng-table>
            @if (canEdit && showAddLayout()) {
                <label>
                    <span class="label-text">Add Layout</span>
                    <s25-ng-dropdown-search-criteria
                        [type]="'locationLayouts'"
                        [filter]="filterLayouts"
                        [noSelection]="noLayoutsPlaceholder"
                        [ariaLabel]="'Add Layout'"
                        (valueChange)="addLayout($event)"
                    />
                </label>
            }
        }
    `,
    host: {
        "[class.read-only]": "!canEdit",
    },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            padding: 1rem;

            .capacity {
                display: flex;
                text-wrap: nowrap;
                gap: 1em;

                s25-ng-editable-number {
                    width: 100%;
                    position: relative;
                    bottom: 6px;
                }
            }

            &.read-only {
                .capacity {
                    justify-content: space-between;

                    s25-ng-editable-number {
                        width: fit-content;
                        position: static;
                    }
                }

                .default-dropdown {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25LocationDetailsLayoutsComponent implements OnInit {
    @Input({ required: true }) layouts: Layout[];
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) maxCapacity: number;

    @ViewChild(S25TableComponent) tableComp: S25TableComponent;
    @ViewChild(S25DropdownSearchCriteriaComponent) searchCriteriaComp: S25DropdownSearchCriteriaComponent;

    private objectDetailsService = inject(S25ObjectDetailsService);

    spaceLayouts = this.objectDetailsService.layouts;
    defaultLayout: DropDownItem = {};
    defaultOptions: DropDownItem[] = [];
    tableConfig: Table.DataSource;
    init = signal(false);
    showAddLayout = signal(false);
    noLayoutsPlaceholder: string;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        const layout = this.layouts.find((layout) => !!layout.defaultLayout);
        if (layout) {
            const { layoutId, layoutName } = layout;
            this.defaultLayout = { itemId: layoutId, itemName: layoutName, txt: layoutName };
        }
        if (this.layouts.length) {
            this.defaultOptions = this.layouts.map((layout) => ({
                itemId: layout.layoutId,
                itemName: layout.layoutName,
            }));
        }
        this.objectDetailsService.setLayouts(this.layouts);
        this.objectDetailsService.setSelectedLayout(layout ?? this.layouts[0]);
        this.initTableConfig();
        this.showAddLayout.set(true);
        this.init.set(true);
    }

    @Bind
    filterLayouts(item: DropDownItem) {
        return !this.spaceLayouts().some((layout) => layout.layoutId === item.itemId);
    }

    getPayloadProp(layoutId: number) {
        return this.defaultLayout.itemId === layoutId ? "default" : "additional";
    }

    @Bind
    async saveCapacity(capacity: number, layoutId?: number) {
        let payload;
        if (layoutId) {
            this.objectDetailsService.setLayoutCapacity(layoutId, capacity);
            payload = {
                id: this.itemId,
                layouts: {
                    [this.getPayloadProp(layoutId)]: { layoutId: layoutId, maxCapacity: capacity },
                },
            };
        } else {
            payload = {
                id: this.itemId,
                maxCapacity: capacity,
            };
        }
        return this.save(payload);
    }

    @Bind
    saveExtraTime(timeCode: string, id: number, prop: "setupTm" | "takedownTm") {
        const payload = {
            id: this.itemId,
            layouts: { [this.getPayloadProp(id)]: [{ layoutId: id, [prop]: timeCode }] },
        };
        return this.save(payload);
    }

    async saveDefault(defaultLayout: DropDownItem) {
        const payload = {
            id: this.itemId,
            layouts: { default: { layoutId: defaultLayout.itemId } },
        };
        return this.save(payload);
    }

    async addLayout(item: DropDownItem) {
        const payload = {
            id: this.itemId,
            layouts: {
                additional: [{ layoutId: item.itemId, maxCapacity: 0 }],
            },
        };
        const error = await this.save(payload);
        if (!error) {
            this.showAddLayout.set(false);
            this.objectDetailsService.setLayouts([
                ...this.spaceLayouts(),
                { layoutId: +item.itemId, layoutName: item.itemName } as Layout,
            ]);
            this.defaultOptions.push({ itemId: item.itemId, itemName: item.itemName });
            return this.refreshTable().then(() => this.showAddLayout.set(true));
        }
    }

    @Bind
    async deleteLayout(layoutId: number) {
        const payload = {
            id: this.itemId,
            remove: { layouts: { [this.getPayloadProp(layoutId)]: [{ layoutId: layoutId }] } },
        };
        const error = await this.save(payload);
        if (!error) {
            this.showAddLayout.set(false);
            this.objectDetailsService.setLayouts(this.spaceLayouts().filter((layout) => layout.layoutId !== layoutId));
            this.defaultOptions = this.defaultOptions.filter((layout) => layout.itemId !== layoutId);
            return this.refreshTable().then(() => this.showAddLayout.set(true));
        }
    }

    refreshTable() {
        this.initTableConfig();
        this.cd.markForCheck();
        return this.tableComp.refresh();
    }

    async save(payload: any) {
        const [_, error] = await S25Util.Maybe(SpaceService.microUpdate(payload));
        if (error) console.error(error);
        return error;
    }

    @Bind
    validateLayoutCap(val: number) {
        if (val > this.maxCapacity)
            return `Layout capacity cannot exceed the max capacity of this location (${this.maxCapacity})`;
        return true;
    }

    initTableConfig() {
        const columns = [
            { id: "name", header: "Name" },
            { id: "cap", header: "Cap" },
            { id: "setup", header: "Setup" },
            { id: "takedown", header: "Takedown" },
            ...(this.canEdit ? [{ id: "remove", header: "Remove" }] : []),
        ];
        this.tableConfig = {
            type: "unpaginated",
            dataSource: () => Promise.resolve({ rows: this.spaceLayouts().map(this.mapToRows) }),
            columns: columns,
        };
    }

    @Bind
    mapToRows(layout: Location.Layout) {
        const { layoutId, layoutName, setupTm, tdownTm, layoutCapacity } = layout;
        return {
            id: layoutId,
            name: layoutName,
            cells: {
                name: {
                    component: GenericTableButtonComponent,
                    inputs: {
                        label: layoutName,
                        type: "link",
                    },
                    outputs: {
                        clicked: () => this.objectDetailsService.setSelectedLayoutById(layoutId),
                    },
                } satisfies Table.Cell<GenericTableButtonComponent>,
                cap: {
                    component: S25EditableNumberComponent,
                    inputs: {
                        val: layoutCapacity,
                        readOnly: !this.canEdit,
                        hasCommit: true,
                        onCommit: (val: number) => this.saveCapacity(val, layoutId),
                        customValidation: this.validateLayoutCap,
                        ariaLabel: `${layoutName} capacity`,
                    },
                },
                setup: {
                    component: S25EditableDurationComponent,
                    inputs: {
                        val: setupTm,
                        hasCommit: true,
                        readOnly: !this.canEdit,
                        onCommit: (val: string) => this.saveExtraTime(val, layoutId, "setupTm"),
                    },
                },
                takedown: {
                    component: S25EditableDurationComponent,
                    inputs: {
                        val: tdownTm,
                        hasCommit: true,
                        readOnly: !this.canEdit,
                        onCommit: (val: string) => this.saveExtraTime(val, layoutId, "takedownTm"),
                    },
                },
                ...(this.canEdit && {
                    remove: {
                        component: S25IconButtonComponent,
                        inputs: {
                            type: "close",
                            ariaLabel: `Remove ${layoutName}`,
                        },
                        outputs: {
                            clicked: () => this.deleteLayout(layoutId),
                        },
                    } satisfies Table.Cell<S25IconButtonComponent>,
                }),
            },
        };
    }
}
